import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type CaseHistoryEntry,
  type DeliveryOutcome,
  type FailureReason,
  type ListUndeliveredParams,
  type ReconfirmParams,
  type RegisteredAddress,
  type RejectParams,
  type ReopenParams,
  type UndeliveredCase,
  type UndeliveredItem,
  type UndeliveredPage,
} from "../../domain/entities/undelivered";
import {
  ListUndeliveredError,
  LoadUndeliveredCaseError,
  ListAddressesError,
  ReconfirmError,
  RejectByCustomerError,
  ReopenRejectionError,
} from "../../domain/errors/errors";
import { addressLine } from "../../../../shared/address/address-line";
import { type UndeliveredRepository } from "../../domain/repos/undelivered-repo";
import {
  type CaseHistoryResponse,
  type DeliveryOutcomeResponse,
  type UndeliveredCaseResponse,
  type UndeliveredItemResponse,
  type UndeliveredListResponse,
  type RegisteredAddressesResponse,
} from "../../infrastructure/responses/undelivered-responses";

const fullName = (c: UndeliveredItemResponse["customer"]): string =>
  c
    ? [c.firstName, c.middleName, c.lastName, c.secondLastName]
        .map((p) => (p ?? "").trim())
        .filter(Boolean)
        .join(" ")
    : "";

const toOutcome = (o?: DeliveryOutcomeResponse | null): DeliveryOutcome | null =>
  o
    ? {
        cycle: o.cycle ?? 1,
        cycleAttempts: o.cycleAttempts ?? 0,
        totalAttempts: o.totalAttempts ?? 0,
        lastFailureReason: (o.lastFailureReason ?? null) as FailureReason | null,
        lastAmbassadorId: o.lastAmbassadorId ?? null,
        lastAmbassadorName: o.lastAmbassadorName ?? null,
        outcomeAt: o.outcomeAt ?? "",
      }
    : null;

const toItem = (r: UndeliveredItemResponse): UndeliveredItem => ({
  requestId: r.requestId,
  customerName: fullName(r.customer),
  customerPhone: r.customer?.phone ?? "",
  zoneName: r.address?.deliveryZoneName ?? null,
  addressText: r.address?.text ?? null,
  createdAt: r.createdAt ?? null,
  outcome: toOutcome(r.outcome),
});

const toHistory = (h: CaseHistoryResponse): CaseHistoryEntry => ({
  fromState: h.fromState ?? null,
  toState: h.toState,
  reason: h.reason ?? null,
  reasonCode: (h.reasonCode ?? null) as FailureReason | null,
  actorId: h.actorId ?? null,
  actorName: h.actorName ?? null,
  createdAt: h.createdAt,
});

export class UndeliveredRepo implements UndeliveredRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async list(params: ListUndeliveredParams): Promise<UndeliveredPage> {
    try {
      const query = new URLSearchParams();
      query.set("status", params.status);
      query.set("page", String(params.page));
      if (params.search) query.set("q", params.search);
      if (params.deliveryZoneId)
        query.set("delivery_zone_id", params.deliveryZoneId);
      const response = await this.apiClient.get<UndeliveredListResponse>(
        `v1/requests/undelivered?${query.toString()}`,
      );
      const data = response.data;
      return {
        items: (data?.items ?? []).map(toItem),
        page: data?.page ?? params.page,
        pageSize: data?.pageSize ?? 10,
        totalItems: data?.totalItems ?? 0,
        totalPages: data?.totalPages ?? 0,
        statusCounts: {
          undeliverable: data?.statusCounts?.undeliverable ?? 0,
          rejected: data?.statusCounts?.rejected ?? 0,
        },
      };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListUndeliveredError(message, apiError);
    }
  }

  async loadCase(requestId: string): Promise<UndeliveredCase> {
    try {
      const response = await this.apiClient.get<UndeliveredCaseResponse>(
        `v1/requests/${requestId}`,
      );
      const data = response.data;
      return {
        requestId,
        customerName: fullName(data?.customer),
        customerPhone: data?.customer?.phone ?? "",
        state: data?.state ?? "",
        addressText: data?.address?.text ?? null,
        zoneName: data?.address?.deliveryZoneName ?? null,
        createdAt: data?.createdAt ?? null,
        history: (data?.history ?? []).map(toHistory),
      };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new LoadUndeliveredCaseError(message, apiError);
    }
  }

  async listAddresses(requestId: string): Promise<RegisteredAddress[]> {
    try {
      const response = await this.apiClient.get<RegisteredAddressesResponse>(
        `v1/requests/${requestId}/addresses`,
      );
      return (response.data?.items ?? []).map((a) => ({
        addressId: a.addressId,
        type: a.type,
        // Composed, not read: an address stored by parts carries no text and
        // the picker printed a blank line for it (DMS-47).
        text: addressLine(a),
        isCurrent: a.isCurrent === true,
      }));
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAddressesError(message, apiError);
    }
  }

  async reconfirm(params: ReconfirmParams): Promise<void> {
    try {
      await this.apiClient.post(
        `v1/requests/${params.requestId}/reconfirmation`,
        {
          deliveryDate: params.deliveryDate,
          deliveryShift: params.deliveryShift,
          addressId: params.addressId || undefined,
          reason: params.reason || undefined,
        },
      );
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ReconfirmError(message, apiError);
    }
  }

  async reject(params: RejectParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${params.requestId}/rejection`, {
        note: params.note,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RejectByCustomerError(message, apiError);
    }
  }

  async reopen(params: ReopenParams): Promise<void> {
    try {
      await this.apiClient.post(
        `v1/requests/${params.requestId}/rejection/reopen`,
        { reason: params.reason },
      );
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ReopenRejectionError(message, apiError);
    }
  }
}
